import type { KnowledgeEditValues } from '../../components/KnowledgeEditModal';
import { tolgee } from 'services/i18n/tolgee';
import { getScopeSpuKey } from '../../model/scope';
import type {
  DocumentKnowledge,
  DocumentKnowledgeScope,
  ProductIdentity,
  ProductKnowledgeDetail,
  ProductKnowledgeProduct,
  ProductKnowledgeProductSearchInput,
  ProductKnowledgeScopeProduct,
  ProductKnowledgeScopeSnapshot,
  ProductKnowledgeType,
  ProductStoreScope,
  SpuKnowledge,
  SupplementalKnowledgeDetail,
} from '../../model/types';
import type { ProductFilterValue } from '../../components/ProductFilter';

export const OVERVIEW_PAGE_SIZE = 20;
export const SCOPE_PRODUCT_PAGE_SIZE = 50;
export const PRODUCT_KNOWLEDGE_TYPES: ProductKnowledgeType[] = [
  'SUPPLEMENTAL_KNOWLEDGE',
  'DOCUMENT_KNOWLEDGE',
  'SPU_DETAIL_KNOWLEDGE',
];

export interface OverviewFilters extends ProductFilterValue {
  selectedStores: General.StoreScope[];
  storeScopes: ProductStoreScope[];
}

export const createDefaultOverviewFilters = (): OverviewFilters => ({
  knowledgePresence: 'ALL',
  keywordType: 'TITLE',
  keyword: '',
  selectedStores: [],
  storeScopes: [],
});

export const countSelectedStores = (scopes: General.StoreScope[]) =>
  scopes.reduce(
    (total, scope) =>
      total +
      scope.regionStoreScopes.reduce(
        (regionTotal, regionScope) => regionTotal + (regionScope.storeIds?.length ?? 0),
        0,
      ),
    0,
  );

export const toProductSearchInput = (
  filters: OverviewFilters,
): ProductKnowledgeProductSearchInput => {
  const keyword = filters.keyword.trim();
  return {
    limit: OVERVIEW_PAGE_SIZE,
    storeScopes: filters.storeScopes,
    knowledgePresence: filters.knowledgePresence,
    knowledgeTypes: PRODUCT_KNOWLEDGE_TYPES,
    ...(keyword ? { keywordType: filters.keywordType, keyword } : {}),
  };
};

export const productIdentityOf = (product: ProductKnowledgeProduct): ProductIdentity => ({
  platform: product.platform,
  region: product.region,
  externalStoreId: product.externalStoreId,
  externalSpuId: product.externalSpuId,
});

export const productIdentityKey = (product: ProductIdentity) =>
  `${product.platform}|${product.region}|${product.externalStoreId}|${product.externalSpuId}`;

export const mergeProductPages = (pages: Array<{ items: ProductKnowledgeProduct[] }>) => {
  const products = new Map<string, ProductKnowledgeProduct>();
  for (const page of pages) {
    for (const product of page.items) products.set(productIdentityKey(product), product);
  }
  return [...products.values()];
};

export const mergeScopeProducts = (groups: ProductKnowledgeScopeProduct[][]) => {
  const products = new Map<string, ProductKnowledgeScopeProduct>();
  for (const group of groups) {
    for (const product of group) products.set(getScopeSpuKey(product), product);
  }
  return [...products.values()];
};

/** 来源短标签（对齐 perseus knowledge.type*），类型页签与知识表来源徽标共用 */
export const knowledgeTypeLabel = (type: ProductKnowledgeType) =>
  ({
    SUPPLEMENTAL_KNOWLEDGE: tolgee.t('productKnowledge_knowledge_type_reception'),
    DOCUMENT_KNOWLEDGE: tolgee.t('productKnowledge_knowledge_type_document'),
    SPU_DETAIL_KNOWLEDGE: tolgee.t('productKnowledge_knowledge_type_product_detail'),
  }[type]);

const emptyScope = (): ProductKnowledgeScopeSnapshot => ({ expand: false, spus: [] });

const snapshotFromDocumentScopes = (
  scopes: DocumentKnowledgeScope[],
): ProductKnowledgeScopeSnapshot => ({
  expand: scopes.some((scope) => scope.scopeType === 'SKU_GROUP'),
  spus: scopes.flatMap((scope) => {
    if (scope.scopeType !== 'SPU' && scope.scopeType !== 'SPU_ID') return [];
    if (!scope.platform || !scope.region || !scope.scopeValue) return [];
    const spuKey = `${scope.scopeValue}|${scope.platform}|${scope.region}`;
    return [{ spuKey, coverAllSkus: true, selectedSkuKeys: [] }];
  }),
});

export interface EditableOverviewKnowledge {
  id: string | number;
  type: ProductKnowledgeType;
  question: string;
  answer: string;
  extraQuestions: string[];
  enabled: boolean;
  scope: ProductKnowledgeScopeSnapshot;
  originalDocumentScopes: DocumentKnowledgeScope[];
}

export const normalizeEditableKnowledge = (
  type: ProductKnowledgeType,
  detail: ProductKnowledgeDetail,
): EditableOverviewKnowledge => {
  if (type === 'SUPPLEMENTAL_KNOWLEDGE') {
    const value = detail as SupplementalKnowledgeDetail;
    return {
      id: value.knowledgeId,
      type,
      question: value.question,
      answer: value.answer,
      extraQuestions: value.extraQuestions,
      enabled: value.enable,
      scope: emptyScope(),
      originalDocumentScopes: [],
    };
  }

  if (type === 'DOCUMENT_KNOWLEDGE') {
    const value = detail as DocumentKnowledge;
    const scopes = value.scopes ?? [];
    return {
      id: value.id,
      type,
      question: value.question ?? '',
      answer: value.answer ?? '',
      extraQuestions: value.extraQuestions ?? [],
      enabled: value.enable,
      scope: value.scope ?? snapshotFromDocumentScopes(scopes),
      originalDocumentScopes: scopes,
    };
  }

  const value = detail as SpuKnowledge;
  return {
    id: value.id,
    type,
    question: value.question,
    answer: value.answer,
    extraQuestions: value.extraQuestions,
    enabled: value.enable,
    // 详情响应可能缺 scope（新 Mapping 契约下不保证携带），兜底空快照防编辑弹窗崩溃
    scope: value.scope ?? emptyScope(),
    originalDocumentScopes: [],
  };
};

export const buildKnowledgeUpdateInput = (
  editable: EditableOverviewKnowledge,
  values: KnowledgeEditValues,
) => {
  // scope 与启停不再随编辑保存提交（对齐 perseus 商品概览页面逻辑优化）
  return {
    question: editable.question,
    answer: values.answer,
    extraQuestions: editable.extraQuestions,
  };
};
